import {
  CalendarCheck,
  CalendarX,
  Clock,
  Banknote,
  AlertCircle,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";

export function PaymentCommitmentCard({
  commitment,
  onRecordPayment,
  onClear,
}) {
  if (!commitment?.committedDate) return null;

  const isPending = commitment.status === "pending";
  const isFulfilled = commitment.status === "fulfilled";
  const isMissed = commitment.status === "missed";

  const committedDate = new Date(commitment.committedDate);
  const today = new Date();
  const daysUntil = Math.ceil((committedDate - today) / (1000 * 60 * 60 * 24));
  const isOverdue = isPending && committedDate < today;

  const config = isFulfilled
    ? {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        icon: CalendarCheck,
        iconColor: "text-emerald-500",
        label: "Commitment Fulfilled",
        labelColor: "text-emerald-700",
      }
    : isMissed
      ? {
          bg: "bg-red-50",
          border: "border-red-200",
          icon: CalendarX,
          iconColor: "text-red-500",
          label: "Commitment Missed",
          labelColor: "text-red-700",
        }
      : isOverdue
        ? {
            bg: "bg-red-50",
            border: "border-red-100",
            icon: AlertCircle,
            iconColor: "text-red-500",
            label: "Payment Overdue",
            labelColor: "text-red-700",
          }
        : {
            bg: "bg-blue-50",
            border: "border-blue-100",
            icon: Clock,
            iconColor: "text-blue-500",
            label: "Payment Expected",
            labelColor: "text-blue-700",
          };

  const Icon = config.icon;

  return (
    <div
      className={cn(
        "rounded-2xl border p-4 space-y-2",
        config.bg,
        config.border,
      )}
    >
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={cn("h-4 w-4", config.iconColor)} />
          <span className={cn("text-xs font-semibold", config.labelColor)}>
            {config.label}
          </span>
          {commitment.isTentative && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/70 text-muted-foreground border">
              Tentative
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground">
            {committedDate.toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </span>
          {!isFulfilled && onClear && (
            <button
              onClick={onClear}
              title="Clear stale commitment"
              className="flex items-center gap-1 text-[9px] font-semibold px-2 py-1 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors border border-red-200"
            >
              <Trash2 className="h-2.5 w-2.5" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Amount + method */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Banknote className="h-3 w-3" />
          {commitment.paymentMethod?.replace(/_/g, " ") ||
            "method not specified"}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn("text-sm font-bold tabular-nums", config.labelColor)}
          >
            ₹{commitment.committedAmount?.toLocaleString()}
          </span>
          {(isPending || isMissed) && (
            <button
              onClick={() =>
                onRecordPayment?.({
                  suggestedAmount: commitment.committedAmount || null,
                  label: "Full Payment",
                })
              }
              className="flex items-center gap-1 text-[9px] font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors border border-emerald-200"
            >
              <Banknote className="h-2.5 w-2.5" />
              Pay
            </button>
          )}
        </div>
      </div>

      {/* Countdown / overdue message */}
      {isPending && !isOverdue && daysUntil >= 0 && (
        <p className="text-[10px] text-blue-600">
          {daysUntil === 0
            ? "Due today"
            : `Due in ${daysUntil} day${daysUntil !== 1 ? "s" : ""}`}
        </p>
      )}
      {(isOverdue || isMissed) && (
        <p className="text-[10px] text-red-600 font-medium">
          {isMissed
            ? `Detected missed on ${new Date(commitment.missedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`
            : `${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? "s" : ""} overdue — follow up now`}
        </p>
      )}
    </div>
  );
}
